import { isValidAreaId } from 'utils/campaign';
import { mapCampaignAreasFromDtoAreas } from 'utils/campaign/mappers';
import type { Campaign, VacancyInfoRequirement } from 'utils/campaign/requirementUtils/types';
import { EMPTY_ARRAY, getHashByCampaign, replaceDirectOnYandexDirect, sortAreas, sortProfRoles } from 'utils/clickme';
import { CAMPAIGN_AVAILABLE_STEPS, CampaignLanguage, CampaignProcess, CampaignSteps } from 'utils/constants/campaign';
import type { BEProfRoles } from 'utils/profRoles';

import { ModelData } from '@hh.ru/magritte-ui';

import { PROF_ROLES, Region, setErirClassificationCatalog, setProfRoles, setRegions } from 'store/autoReducers';
import type { Action } from 'store/createReducer';
import { resetAiImageGeneration } from 'store/models/aiImageGeneration';
import { resetBannerTextGeneration, setBannerDefaultText } from 'store/models/bannerTextGeneration';
import {
    resetBannerInfo,
    setBannerLink,
    setBannerText,
    setBannerTitle,
    setBannerVacancyId,
    setVacancyName,
} from 'store/models/createBanner';
import {
    resetCampaignInfo,
    setCampaignAge,
    setCampaignAutoSource,
    setCampaignBid,
    setCampaignDailyBudget,
    setCampaignExcludedRegions,
    setCampaignFormStep,
    setCampaignGender,
    setCampaignHash,
    setCampaignId,
    setCampaignLanguage,
    setCampaignProcess,
    setCampaignProfessions,
    setCampaignRegions,
    setCampaignSalary,
    setCampaignSavedDeviceType,
    setCampaignSavedSources,
    setCampaignSubSource,
    setCampaignTitle,
    setCampaignTotalBudget,
    setCampaignTotalBudgetNoLimits,
    setCampaignUnknownAge,
    setCampaignVacancyId,
    setIsShownDailyBudget,
} from 'store/models/createCampaign';
import { setRequisitesFilled } from 'store/models/requisites';

type GetAdCreateCampaignBatchActions = (props: {
    regions: Region[];
    mainMenuFormData: {
        vacancyId: number | null;
        link: string | null;
    };
    vacancyInfo: VacancyInfoRequirement;
    [PROF_ROLES]?: BEProfRoles;
    existRequisites: boolean;
    erirClassificationCatalog: ModelData[];
}) => Action[];

export const getAdCreateCampaignBatchActions: GetAdCreateCampaignBatchActions = ({
    regions,
    mainMenuFormData,
    vacancyInfo,
    profRoles,
    existRequisites,
    erirClassificationCatalog,
}) => {
    const actions: Action[] = [
        setRegions(sortAreas(regions)),
        resetCampaignInfo(null),
        resetBannerInfo(null),
        setRequisitesFilled(existRequisites),
        setErirClassificationCatalog(erirClassificationCatalog),
        resetAiImageGeneration(null),
        resetBannerTextGeneration(),
    ];

    const { vacancyId, link } = mainMenuFormData;

    if (vacancyId) {
        actions.push(setBannerVacancyId(vacancyId));
    }

    if (vacancyId && vacancyInfo) {
        const { title, text, url, areaId, profRoleIds } = vacancyInfo;
        actions.push(
            setBannerTitle(title),
            setBannerText(text),
            setBannerLink(url),
            setCampaignRegions(isValidAreaId(areaId) ? [`${areaId}`] : EMPTY_ARRAY),
            setCampaignProfessions(profRoleIds.map(String)),
            setBannerDefaultText(text),
            setVacancyName(title)
        );
    } else if (link) {
        actions.push(setBannerLink(link));
    }

    if (profRoles) actions.push(setProfRoles(sortProfRoles(profRoles.professionalCategories)));

    return actions;
};

type GetAdEditCampaignBatchActions = (props: {
    campaign: Campaign;
    step?: string;
    campaignProcess?: CampaignProcess;
}) => Action[];

export const getAdEditOrCopyCampaignBatchActions: GetAdEditCampaignBatchActions = ({
    campaign,
    step,
    campaignProcess = CampaignProcess.Edit,
}) => {
    const {
        id,
        areas,
        sex,
        apiSources,
        ageMin,
        ageMax,
        budget,
        budgetDaily,
        title,
        salaryRange,
        language,
        deviceType,
        includeAnonymousAge,
        vacancyId,
        autoSource,
        bid,
        professionalRoles,
    } = campaign;

    const { include, exclude } = mapCampaignAreasFromDtoAreas(areas);

    const newApiSources = replaceDirectOnYandexDirect(apiSources);
    const campaignTotalBudgetNoLimits = Boolean(id && !budget);

    const editActions: Action[] = [
        setCampaignRegions(include),
        setCampaignExcludedRegions(exclude),
        setCampaignProfessions(professionalRoles?.map(String) ?? []),
        setCampaignGender(sex),
        setCampaignSubSource(newApiSources),
        setCampaignSavedSources(newApiSources),
        setCampaignAge({ adAgeFrom: ageMin, adAgeTo: ageMax }),
        setCampaignTotalBudget(budget ? `${budget}` : ''),
        setCampaignTotalBudgetNoLimits(campaignTotalBudgetNoLimits),
        setCampaignDailyBudget(budgetDaily ? `${budgetDaily}` : ''),
        setIsShownDailyBudget(!!budgetDaily),
        setCampaignTitle(title),
        setCampaignId(id),
        setCampaignSalary(salaryRange),
        setCampaignLanguage(language.map((lang) => lang.toUpperCase()) as CampaignLanguage[]),
        setCampaignSavedDeviceType(deviceType),
        setCampaignUnknownAge(includeAnonymousAge),
        setCampaignVacancyId(vacancyId),
        setCampaignHash(getHashByCampaign(campaign)),
        setCampaignAutoSource(autoSource),
        setCampaignProcess(campaignProcess),
    ];

    if (bid) {
        editActions.push(setCampaignBid(bid));
    }

    if (step && CAMPAIGN_AVAILABLE_STEPS.includes(step as CampaignSteps)) {
        editActions.push(setCampaignFormStep(parseInt(step, 10)));
    }

    return editActions;
};
